/**
 * OverviewTab — Accounts landing tab.
 *
 * Phase 86: every number on this tab now comes from the single canonical
 * `/api/position` response. Group subtotals are summed from the exact same
 * resource rows that produced the headline, so a group total and the hero can
 * never disagree — and any account the resolver deliberately excluded is
 * labelled as such instead of quietly vanishing from a total.
 *
 * Sections (top → bottom):
 *   1. PositionHero — net worth, assets, liabilities, cash, invested, trend
 *   2. Attention — real data problems only (stale balances, payments due)
 *   3. NetWorthSection — asset/liability composition charts
 *   4. Accounts grouped by financial role
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import AccountRow from './AccountRow';
import NetWorthSection from './NetWorthSection';
import PositionHero from './PositionHero';
import MoneyByPurposeCard from './MoneyByPurposeCard';
import ConnectedToPlansCard from './ConnectedToPlansCard';
import ManageAssignmentsDialog from './ManageAssignmentsDialog';
import { fetchGoalAllocations, fetchMoneyPurpose } from '@/lib/allocationsApi';
import { formatMoney } from '@/lib/format';
import { ACCOUNTS_TONE } from '@/lib/accountsDemo';

export default function OverviewTab({
  accounts = [],
  position = null,
  history = null,
  positionLoading = false,
  positionError = null,
  attentionItems = [],
  onSelectAccount,
  baseCurrency = 'INR',
}) {
  // Phase 86.D — purpose buckets and goal allocations. Both derive from the
  // same canonical position as the hero, so nothing here can disagree with it.
  const [purpose, setPurpose]       = useState(null);
  const [plans, setPlans]           = useState(null);
  const [allocLoading, setAlloc]    = useState(true);
  const [manageOpen, setManageOpen] = useState(false);

  const loadAllocations = useCallback(async () => {
    setAlloc(true);
    const [mp, ga] = await Promise.all([fetchMoneyPurpose(), fetchGoalAllocations()]);
    setPurpose(mp);
    setPlans(ga);
    setAlloc(false);
  }, []);

  useEffect(() => {
    loadAllocations();
    const onRefresh = () => loadAllocations();
    window.addEventListener('accounts:refresh', onRefresh);
    return () => window.removeEventListener('accounts:refresh', onRefresh);
  }, [loadAllocations]);

  const cash        = accounts.filter((a) => a.category === 'cash');
  const investments = accounts.filter((a) => a.category === 'investments');
  const liabilities = accounts.filter((a) => a.category === 'liabilities');

  const base = position?.base_currency || baseCurrency;

  // Index the canonical resources by id. Group subtotals are derived from these
  // rows — never recomputed from the client list — which is what keeps this tab
  // arithmetically consistent with the hero above it.
  const byId = new Map(
    (position?.included_resources || []).map((r) => [String(r.id), r]),
  );
  const excludedById = new Map(
    (position?.excluded_resources || []).map((r) => [String(r.id), r]),
  );
  const groupTotal = (rows) => rows.reduce(
    (sum, a) => sum + Math.abs(byId.get(String(a.id))?.base_amount || 0), 0,
  );

  // Stale balances are data problems the user can act on, so they belong in
  // Attention alongside upcoming payments. Never-confirmed balances are an
  // onboarding gap rather than an alarm, so they collapse into a single quiet
  // row instead of shouting once per account.
  const freshnessItems = (position?.freshness?.issues || []).map((i) => ({
    label: `${i.name} — ${i.label}`,
    sub: i.detail || 'Confirm the balance to restore confidence in your totals.',
    urgency: i.status === 'stale_vs_transactions' ? 'high' : 'medium',
  }));
  const unconfirmedCount = position?.freshness?.unconfirmed_count || 0;
  const unconfirmedItem = unconfirmedCount > 0 ? [{
    label: `${unconfirmedCount} balance${unconfirmedCount === 1 ? '' : 's'} not confirmed yet`,
    sub: (position.freshness.unconfirmed || []).slice(0, 3).map((u) => u.name).join(', ')
      + (unconfirmedCount > 3 ? ` and ${unconfirmedCount - 3} more` : '')
      + ' — set a balance so these totals can be trusted.',
    urgency: 'low',
  }] : [];
  const allAttention = [...freshnessItems, ...attentionItems, ...unconfirmedItem];

  return (
    <div className="space-y-6" data-testid="accounts-overview">
      <PositionHero position={position} history={history} loading={positionLoading}
                    error={positionError} />

      {allAttention.length > 0 && (
        <section className="bg-card rounded-xl border border-border overflow-hidden shadow-sm" data-testid="accounts-attention">
          <header className="px-5 py-3.5 border-b border-border/60 bg-offwhite/60 flex items-center gap-2">
            <AlertCircle className="h-3.5 w-3.5" style={{ color: ACCOUNTS_TONE.warning }} />
            <p className="text-xs font-semibold text-charcoal">Needs attention</p>
          </header>
          <ul className="divide-y divide-border/40">
            {allAttention.map((item, i) => (
              <li key={i} className="flex items-start gap-3 px-5 py-3.5" data-testid={`accounts-attention-item-${i}`}>
                <span
                  className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
                  style={{ backgroundColor: item.urgency === 'high'
                    ? ACCOUNTS_TONE.warning
                    : item.urgency === 'low' ? '#B8B8B0' : ACCOUNTS_TONE.liability }}
                />
                <div>
                  <p className="text-sm font-medium text-charcoal">{item.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{item.sub}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* What the money is FOR, and which plans it serves. These answer the
          human questions; the composition charts below answer the analytical
          one. */}
      <div className="grid lg:grid-cols-2 gap-4 items-start">
        <MoneyByPurposeCard
          data={purpose}
          loading={allocLoading}
          onManage={() => setManageOpen(true)}
        />
        <ConnectedToPlansCard
          data={plans}
          loading={allocLoading}
          onManage={() => setManageOpen(true)}
        />
      </div>

      {/* Composition charts. Fed from the canonical composition so the slices
          always add up to the headline assets/liabilities. */}
      <NetWorthSection position={position} baseCurrency={base} />

      <ManageAssignmentsDialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        accounts={accounts}
        onSaved={() => {
          loadAllocations();
          window.dispatchEvent(new Event('accounts:refresh'));
        }}
      />

      {/* Grouped account summaries */}
      <div className="space-y-4">
        {[
          { key: 'cash',        label: 'Cash & savings', accounts: cash,        color: 'hsl(var(--evergreen))', negative: false },
          { key: 'investments', label: 'Investments',    accounts: investments, color: ACCOUNTS_TONE.invested,  negative: false },
          { key: 'liabilities', label: 'Liabilities',    accounts: liabilities, color: ACCOUNTS_TONE.liability, negative: true },
        ].filter((g) => g.accounts.length > 0).map((group) => (
          <section
            key={group.key}
            className="bg-card rounded-xl border border-border overflow-hidden shadow-sm"
            data-testid={`accounts-group-${group.key}`}
          >
            <header className="flex items-center justify-between px-5 py-3.5 border-b border-border/60 bg-offwhite/60">
              <p className="text-xs font-semibold text-charcoal">{group.label}</p>
              <p className="num text-sm font-semibold" style={{ color: group.color }}
                 data-testid={`accounts-group-total-${group.key}`}>
                {group.negative ? '−' : ''}{formatMoney(groupTotal(group.accounts), base)}
                <span className="text-muted-foreground font-normal ml-1.5">
                  · {group.accounts.length} {group.accounts.length === 1 ? 'account' : 'accounts'}
                </span>
              </p>
            </header>
            <div className="divide-y divide-border/40">
              {group.accounts.map((a) => (
                <AccountRow
                  key={a.id}
                  account={a}
                  resource={byId.get(String(a.id)) || null}
                  excluded={excludedById.get(String(a.id)) || null}
                  onClick={() => onSelectAccount?.(a)}
                  baseCurrency={base}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
